import {
   ArrowLeftMdIcon,
   ChevronRightIcon,
   VolumeMaxIcon,
} from '@forwardflow/icons'
import { useGetVoices } from '@forwardflow/queries/campaign.queries'
import { useOnboardingStoreV2 } from '@forwardflow/store'
import {
   Button,
   Checkbox,
   ContentCard,
   Grid,
   Group,
   Stack,
} from '@forwardflow/ui-v2'
import VoiceAvatar1 from '@forwardflow/assets/onboarding/voice1.png'
import VoiceAvatar2 from '@forwardflow/assets/onboarding/voice2.png'
import { motion } from 'framer-motion'
import cn from 'classnames'
import { useState, useRef, useEffect, useMemo } from 'react'
import CampaignService from '@forwardflow/services/CampaignService'

type TVoiceAssistant = {
   voiceId: string
   lastUpdated: string
   name: string
   description: string
   avatar: string
   gender: string
}

const VOICES: TVoiceAssistant[] = [
   {
      voiceId: 'P7x743VjyZEOihNNygQ9',
      lastUpdated: '2025-08-26T16:04:53.948Z',
      name: 'Dakotah',
      description: 'Hello I am Dakotah H, your real state agent',
      avatar:
         'https://forward-flow-assets.s3.us-west-2.amazonaws.com/campaign-avatars/assistant-4.png',
      gender: 'female',
   },
   {
      voiceId: 'f5HLTX707KIM4SzJYzSz',
      lastUpdated: '2025-10-30T19:08:35.834Z',
      name: 'Bradward',
      description: 'male telecom',
      avatar:
         'https://forward-flow-assets.s3.us-west-2.amazonaws.com/campaign-avatars/assistant-2.png',
      gender: 'male',
   },
]

// const PRICES = {
//    Dakotah: '0.02/Minute',
//    Bradward: '0.025/Minute',
// }

const AVATARS = [VoiceAvatar1, VoiceAvatar2]

export const VoiceAssistant = () => {
   const {
      getNextStep,
      getPreviousStep,
      setCampaignConfiguration,
      onboardingData,
   } = useOnboardingStoreV2()
   const { data: voices } = useGetVoices()

   const filteredVoices: TVoiceAssistant[] = useMemo(() => {
      const recommendedVoices = voices?.filter((voice) =>
         ['kdnRe2koJdOK4Ovxn2DI', 'f5HLTX707KIM4SzJYzSz'].includes(
            voice.voiceId
         )
      )
      return recommendedVoices?.length && recommendedVoices?.length > 0
         ? recommendedVoices
         : VOICES
   }, [voices])

   const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null)
   const [loadingVoiceId, setLoadingVoiceId] = useState<string | null>(null)
   const [audioUrls, setAudioUrls] = useState<Record<string, string>>({})
   const fetchedVoicesRef = useRef<Set<string>>(new Set())
   const audioUrlsRef = useRef<Record<string, string>>({})
   const audioRef = useRef<HTMLAudioElement | null>(null)

   // Keep ref in sync with state
   useEffect(() => {
      audioUrlsRef.current = audioUrls
   }, [audioUrls])

   // Cleanup audio and blob URLs on unmount (when navigating away)
   useEffect(() => {
      return () => {
         // Stop any playing audio
         if (audioRef.current) {
            audioRef.current.pause()
            audioRef.current.currentTime = 0
            audioRef.current = null
         }

         // Revoke all blob URLs
         Object.values(audioUrlsRef.current).forEach((url) => {
            URL.revokeObjectURL(url)
         })
      }
   }, [])

   const playAudio = (voiceId: string, url: string) => {
      const audio = new Audio(url)
      audioRef.current = audio
      setPlayingVoiceId(voiceId)

      audio.onended = () => {
         setPlayingVoiceId(null)
         audioRef.current = null
      }

      audio.onerror = () => {
         setPlayingVoiceId(null)
         audioRef.current = null
      }

      audio.play()
   }

   const handlePlayVoice = async (voiceId: string, e: React.MouseEvent) => {
      e.stopPropagation()
      console.log('handlePlayVoice')

      // Don't allow action if this voice is currently loading
      if (loadingVoiceId === voiceId) {
         return
      }

      // If already playing this voice, stop it
      if (playingVoiceId === voiceId && audioRef.current) {
         console.log('stop')
         audioRef.current.pause()
         audioRef.current.currentTime = 0
         audioRef.current = null
         setPlayingVoiceId(null)
         return
      }

      // Stop any currently playing audio
      if (audioRef.current) {
         audioRef.current.pause()
         audioRef.current.currentTime = 0
         audioRef.current = null
         setPlayingVoiceId(null)
      }

      // If audio URL is already cached, use it
      if (audioUrls[voiceId]) {
         playAudio(voiceId, audioUrls[voiceId])
         return
      }

      // Don't fetch if already trying to fetch
      if (fetchedVoicesRef.current.has(voiceId)) {
         return
      }

      // Fetch audio on-demand (only once per voice)
      try {
         setLoadingVoiceId(voiceId)
         fetchedVoicesRef.current.add(voiceId)

         const response =
            await CampaignService.getExternalElevenLabsVoicePreview(voiceId)

         if (response?.data && response.data.byteLength > 0) {
            const blob = new Blob([response.data], { type: 'audio/mpeg' })
            const url = URL.createObjectURL(blob)

            // Cache the URL
            setAudioUrls((prev) => ({ ...prev, [voiceId]: url }))

            // Play the audio
            playAudio(voiceId, url)
         } else {
            // No data received - remove from fetched set to allow retry
            fetchedVoicesRef.current.delete(voiceId)
         }
      } catch (error) {
         // Error loading audio - remove from fetched set to allow retry
         fetchedVoicesRef.current.delete(voiceId)
         setPlayingVoiceId(null)
      } finally {
         setLoadingVoiceId(null)
      }
   }

   return (
      <Stack gap={32}>
         <ContentCard className="space-y-6">
            <p className="text-white font-semibold text-lg ">
               Select Assistant Voices
            </p>

            <div className="grid grid-cols-2 gap-4">
               {filteredVoices?.map((voice, index) => {
                  const isSelected =
                     onboardingData?.campaignConfiguration?.ai_assistants?.some(
                        (assistant) => assistant.voice_id === voice.voiceId
                     )
                  return (
                     <motion.button
                        key={index}
                        className={cn(
                           'rounded-[16px] border text-left',
                           isSelected
                              ? 'border-[#9F7BFF]/40 bg-[#9F7BFF]/10'
                              : 'border-white/20 bg-white/[.02]'
                        )}
                        onClick={() =>
                           setCampaignConfiguration({
                              ai_assistants: [
                                 {
                                    id: index + 1,
                                    voice_id: voice.voiceId,
                                    assistant_name: voice.name,
                                 },
                              ],
                           })
                        }
                     >
                        <div className="border-b border-white/20 p-6 flex flex-col items-center gap-4">
                           <div className="relative">
                              <img
                                 src={AVATARS[index]}
                                 alt={voice.name}
                                 className={cn(
                                    'w-60 relative z-10 transition-all duration-300',
                                    playingVoiceId === voice.voiceId &&
                                       'scale-105'
                                 )}
                              />
                              {playingVoiceId === voice.voiceId && (
                                 <>
                                    <div className="absolute inset-0 rounded-full bg-[#9F7BFF] blur-2xl opacity-60 animate-pulse pointer-events-none" />
                                    <div className="absolute inset-0 rounded-full bg-[#9F7BFF] blur-3xl opacity-40 animate-ping pointer-events-none" />
                                 </>
                              )}
                           </div>
                           <p className="text-white font-semibold text-lg">
                              {voice.name}
                           </p>
                           <button
                              className={cn(
                                 'rounded-[16px] flex items-center gap-2 border p-2 px-4 text-white transition-all',
                                 loadingVoiceId === voice.voiceId
                                    ? 'opacity-50 cursor-wait'
                                    : 'cursor-pointer hover:bg-white/[.08]',
                                 playingVoiceId === voice.voiceId
                                    ? 'border-[#9F7BFF] bg-[#9F7BFF]/20'
                                    : 'border-white/15 bg-white/[.02]'
                              )}
                              onClick={(e) => {
                                 handlePlayVoice(voice.voiceId, e)
                              }}
                           >
                              <VolumeMaxIcon width={18} />
                              {loadingVoiceId === voice.voiceId
                                 ? 'Loading...'
                                 : playingVoiceId === voice.voiceId
                                 ? 'Stop Voice'
                                 : 'Play Voice'}
                           </button>
                           {/* <p className="text-white/70 text-sm">
                              ${PRICES[voice.name as keyof typeof PRICES]}
                           </p> */}
                        </div>
                        <Group className="p-6" justify="center">
                           <Checkbox size="sm" checked={isSelected} readOnly />
                           <p className="text-white font-semibold">
                              Select Voice
                           </p>
                        </Group>
                     </motion.button>
                  )
               })}
            </div>
         </ContentCard>
         <Grid>
            <Grid.Col span={3}>
               <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => getPreviousStep()}
               >
                  <ArrowLeftMdIcon width={12} /> Back
               </Button>
            </Grid.Col>
            <Grid.Col span={9}>
               <Button
                  className="w-full"
                  onClick={() => getNextStep()}
                  disabled={
                     !onboardingData?.campaignConfiguration?.ai_assistants
                        ?.length
                  }
               >
                  Confirm and Continue <ChevronRightIcon width={18} />
               </Button>
            </Grid.Col>
         </Grid>
      </Stack>
   )
}
